import React, { useCallback, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ChatModal from '../components/ChatModal';
import './Messages.css';
import { apiUrl } from '../api';

function Messages() {
  const [groupedConversations, setGroupedConversations] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Drill-down states
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [isChatOpen, setIsChatOpen] = useState(false);

  const token = localStorage.getItem('token');
  const currentUserId = localStorage.getItem('userId');
  const navigate = useNavigate();

  const fetchConversations = useCallback(async () => {
    try {
      const response = await fetch(apiUrl('/api/messages'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const responseText = await response.text();

      if (response.ok) {
        try {
          const data = JSON.parse(responseText);
          // Group by product_id
          const grouped = data.reduce((acc, conversation) => {
            const key = conversation.product_id || 'general';
            if (!acc[key]) {
              acc[key] = {
                productId: conversation.product_id,
                productTitle: conversation.product_title || 'General Chat',
                threads: [],
                totalUnread: 0
              };
            }

            acc[key].threads.push(conversation);
            acc[key].totalUnread += (conversation.unread_count || 0);
            return acc;
          }, {});

          setGroupedConversations(grouped);
        } catch (parseError) {
          console.error('Failed to parse conversations response:', parseError);
          setError('Failed to parse server response');
        }
      } else {
        setError('Failed to load conversations - Status: ' + response.status);
      }
    } catch (err) {
      console.error('Error fetching conversations:', err);
      setError('Failed to load conversations: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }

    fetchConversations();
    const interval = setInterval(fetchConversations, 3000);
    return () => clearInterval(interval);
  }, [token, navigate, fetchConversations]);

  const handleOpenChat = (conversation) => {
    setSelectedConversation(conversation);
    setIsChatOpen(true);
  };

  const handleCloseChat = () => {
    setIsChatOpen(false);
    setSelectedConversation(null);
    fetchConversations();
  };

  if (loading) {
    return <div className="messages-page"><p className="loading-text">Loading conversations...</p></div>;
  }

  const activeGroup = selectedProductId ? groupedConversations[selectedProductId] : null;

  return (
    <div className="messages-page">
      <div className="messages-container">
        
        {/* Navigation Header / Breadcrumb */}
        <div className="messages-header">
          {selectedProductId ? (
            <div className="breadcrumb-nav">
              <button className="back-btn" onClick={() => setSelectedProductId(null)}>
                ← Back to All Listings
              </button>
              <h2>{activeGroup?.productTitle || 'Listing Messages'}</h2>
            </div>
          ) : (
            <h1>Listing Inbox</h1>
          )}
        </div>

        {error && <div className="error-message">{error}</div>}

        {Object.keys(groupedConversations).length === 0 ? (
          <div className="no-conversations">
            <p>No conversations yet</p>
          </div>
        ) : (
          <div className="conversations-content">
            
            {/* VIEW 1: LISTING CARDS (Top-Level) */}
            {!selectedProductId && (
              <div className="listings-grid">
                {Object.values(groupedConversations).map((group) => (
                  <div 
                    key={group.productId || 'general'} 
                    className={`listing-card ${group.totalUnread > 0 ? 'has-unread' : ''}`}
                    onClick={() => setSelectedProductId(group.productId || 'general')}
                  >
                    <div className="listing-card-icon">📦</div>
                    <div className="listing-card-details">
                      <h3>{group.productTitle}</h3>
                      <p>{group.threads.length} {group.threads.length === 1 ? 'Customer inquiry' : 'Customers inquiring'}</p>
                    </div>
                    {group.totalUnread > 0 && (
                      <div className="unread-badge">{group.totalUnread}</div>
                    )}
                    <div className="chevron-icon">→</div>
                  </div>
                ))}
              </div>
            )}

            {/* VIEW 2: CUSTOMERS FOR SELECTED LISTING */}
            {selectedProductId && activeGroup && (
              <div className="customers-list">
                {activeGroup.threads.map((conversation) => (
                  <div
                    key={`${conversation.other_user_id}-${conversation.product_id || 'general'}`}
                    className={`customer-item ${conversation.unread_count > 0 ? 'unread' : ''}`}
                    onClick={() => handleOpenChat(conversation)}
                  >
                    <div className="customer-avatar">
                      {conversation.other_username ? conversation.other_username.charAt(0).toUpperCase() : 'U'}
                    </div>
                    
                    <div className="customer-info">
                      <div className="customer-header">
                        <h4>{conversation.other_username}</h4>
                        <span className="conversation-time">
                          {new Date(conversation.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="conversation-preview">
                        {conversation.message_text?.length > 60
                          ? conversation.message_text.substring(0, 60) + '...'
                          : conversation.message_text}
                      </p>
                    </div>

                    {conversation.unread_count > 0 && (
                      <div className="unread-badge">{conversation.unread_count}</div>
                    )}
                  </div>
                ))}
              </div>
            )}

          </div>
        )}
      </div>

      {selectedConversation && (
        <ChatModal
          isOpen={isChatOpen}
          onClose={handleCloseChat}
          productId={selectedConversation.product_id}
          receiverId={selectedConversation.other_user_id}
          receiverUsername={selectedConversation.other_username}
          currentUserId={currentUserId}
        />
      )}
    </div>
  );
}

export default Messages;